import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import type { User as SupabaseUser } from "@supabase/supabase-js";
import { supabase } from "../supabaseClient";
import {
  getPostLoginRouteByRole,
  normalizeOnboardingStep,
  normalizeRole,
  normalizeStaffSubRole,
  type AppRole,
  type ProviderOnboardingStep,
  type StaffSubRole,
} from "./roleRoutes";

export type AppUser = {
  id: string;
  name: string;
  email: string;
  role: Exclude<AppRole, "guest"> | null;
  staffSubRole?: StaffSubRole | null;
  providerId?: string | null;
  providerStatus?: string;
  providerOnboardingCompleted?: boolean;
  providerOnboardingStep?: ProviderOnboardingStep;
};

type AuthContextValue = {
  user: AppUser | null;
  token: string | null;
  role: AppRole;
  sessionLoading: boolean;
  login: (user: AppUser, token: string) => void;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const readString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }
  return undefined;
};

const readBoolean = (...values: unknown[]): boolean | undefined => {
  for (const value of values) {
    if (typeof value === "boolean") {
      return value;
    }
  }
  return undefined;
};

export const toAppUser = (
  supabaseUser: SupabaseUser,
  accessToken: string,
): { user: AppUser; token: string } => {
  const metadata = supabaseUser.user_metadata ?? {};
  const appMetadata = supabaseUser.app_metadata ?? {};

  const normalizedRoleCandidates = [
    metadata.role,
    appMetadata.role,
    metadata.userRole,
    appMetadata.userRole,
    metadata.requested_role,
    appMetadata.requested_role,
    metadata.accountRole,
    appMetadata.accountRole,
    metadata.account_type,
    appMetadata.account_type,
    metadata.user_type,
    appMetadata.user_type,
    metadata.profileType,
    appMetadata.profileType,
  ];

  const normalizedRole =
    normalizedRoleCandidates
      .map((value) => normalizeRole(value))
      .find((role) => role !== "guest") ?? "guest";

  const role = normalizedRole === "guest" ? null : normalizedRole;

  const staffSubRole =
    normalizeStaffSubRole(metadata.internal_role) ??
    normalizeStaffSubRole(metadata.staffRole) ??
    normalizeStaffSubRole(metadata.role) ??
    normalizeStaffSubRole(appMetadata.internal_role) ??
    normalizeStaffSubRole(appMetadata.staffRole) ??
    normalizeStaffSubRole(appMetadata.role);

  return {
    user: {
      id: supabaseUser.id,
      name:
        readString(
          metadata.full_name,
          metadata.name,
          appMetadata.full_name,
          appMetadata.name,
          supabaseUser.email,
        ) ?? "User",
      email: supabaseUser.email ?? "",
      role,
      staffSubRole,
      providerId: readString(
        metadata.providerId,
        appMetadata.providerId,
        metadata.provider_id,
        appMetadata.provider_id,
      ) ?? null,
      providerStatus: readString(
        metadata.providerStatus,
        appMetadata.providerStatus,
      ),
      providerOnboardingCompleted:
        readBoolean(
          metadata.providerOnboardingCompleted,
          appMetadata.providerOnboardingCompleted,
        ) ?? false,
      providerOnboardingStep:
        normalizeOnboardingStep(metadata.providerOnboardingStep) ??
        normalizeOnboardingStep(appMetadata.providerOnboardingStep),
    },
    token: accessToken,
  };
};

export const getPostLoginRoute = (user: AppUser | null): string => {
  if (!user?.role) {
    return "/login";
  }

  return getPostLoginRouteByRole(user.role, user.staffSubRole ?? null);
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);

  const applySession = useCallback(
    (session: { user: SupabaseUser; access_token: string } | null) => {
      if (!session) {
        setUser(null);
        setToken(null);
        return;
      }

      const mapped = toAppUser(session.user, session.access_token);
      setUser(mapped.user);
      setToken(mapped.token);
    },
    [],
  );

  const refreshSession = useCallback(async () => {
    setSessionLoading(true);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      applySession(session);
    } finally {
      setSessionLoading(false);
    }
  }, [applySession]);

  useEffect(() => {
    let active = true;

    const bootstrap = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!active) {
          return;
        }

        applySession(session);
      } finally {
        if (active) {
          setSessionLoading(false);
        }
      }
    };

    void bootstrap();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!active) {
        return;
      }

      applySession(session);
      setSessionLoading(false);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [applySession]);

  const login = useCallback((nextUser: AppUser, nextToken: string) => {
    setUser(nextUser);
    setToken(nextToken);
    setSessionLoading(false);
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
    setToken(null);
    window.location.assign("/");
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      role: user?.role ?? "guest",
      sessionLoading,
      login,
      logout,
      refreshSession,
    }),
    [user, token, sessionLoading, login, logout, refreshSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = (): AuthContextValue => {
  const ctx = useContext(AuthContext);

  if (!ctx) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }

  return ctx;
};